'use client'

import * as React from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { gsap } from 'gsap'
import { toast } from 'sonner'

import { AlertIcon } from '~/sotano-kit/icons/alert'
import { GitHubIcon } from '~/sotano-kit/icons/github'
import { GoogleIcon } from '~/sotano-kit/icons/google'
import { loginRoute } from '~/constants/routing'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { Separator } from '~/sotano-kit/primitives/separator'
import { EmailLoginProvider, EmailStep } from './email-login-provider'
import { LoginFormResponseContainer } from './login-form-response'
import { SocialProviderLoginButton } from './login-social-provider-button'
import { useSignIn, useSignUp } from '@clerk/nextjs'
import { BlurredLogo } from '~/components/mkt/blurred-logo'
import { Heading } from '~/sotano-kit/primitives/heading'
import { getLoginHref } from './get-login-href'
import { clsx } from 'clsx'
import { useHasRendered } from '~/hooks/use-has-rendered'

export type AuthFormProps = { isSignUp?: boolean }

export const AuthForm: React.FC<AuthFormProps> = ({ isSignUp = false }) => {
  const { isLoaded, signIn, setActive } = useSignIn()
  const { signUp } = useSignUp()

  const router = useRouter()
  const [loadingProvider, setLoadingProvider] = React.useState<
    'google' | 'github' | null
  >(null)
  const [notAllowed, setNotAllowed] = React.useState(false)
  const searchParams = useSearchParams()
  const [emailStep, setEmailStep] = React.useState<EmailStep>('disabled')

  const loadingProviderFromQuery = searchParams?.get('provider-loading')

  // entrance
  React.useEffect(() => {
    if (notAllowed) return
    const tl = gsap.timeline()
    tl.to('[data-enter]', {
      autoAlpha: 1,
    })

    return () => {
      tl.kill()
    }
  }, [notAllowed])

  const handleSignInWithProvider = React.useCallback(
    async (provider: 'google' | 'github') => {
      if (!isLoaded) return
      setLoadingProvider(provider)

      try {
        const next = searchParams.get('next')
        await signIn.create({
          strategy: `oauth_${provider}`,
          redirectUrl: getLoginHref(`/login?provider-loading=${provider}`),
          actionCompleteRedirectUrl: next ? decodeURIComponent(next) : '/login', // easy fix
        })
        if (signIn.firstFactorVerification.externalVerificationRedirectURL) {
          window.location.href =
            signIn.firstFactorVerification.externalVerificationRedirectURL.href
        }
      } catch (error) {
        console.error(error)
        toast.error(`Error signing in with ${provider}`)
      }
    },
    [isLoaded, signIn, searchParams]
  )

  // catch sign in with provider errors here
  React.useEffect(() => {
    if (!isLoaded) return
    if (!signIn?.status) return

    if (!signIn.firstFactorVerification.strategy?.startsWith('oauth_')) return

    switch (signIn.status) {
      case 'complete':
        setActive({ session: signIn.createdSessionId }).then(() => {
          router.refresh()
        })
        break

      case 'needs_identifier':
        if (signIn.firstFactorVerification.status === 'transferable') {
          // it's transferable, meaning, we should be creating a user here, as it doesn't exist
          if (!signUp) throw new Error('Sign up not ready')
          signUp.create({ transfer: true }).then(async (result) => {
            await setActive({ session: result.createdSessionId })
            router.refresh()
          })
        } else if (signIn.firstFactorVerification.status === 'unverified') {
          if (
            signIn.firstFactorVerification.error?.code === 'not_allowed_access'
          ) {
            setNotAllowed(true)
          }
        }
        break
      default:
        console.error(`Unhandled log in status: ${signIn.status}`)
        break
    }
  }, [signIn, isLoaded, setActive, signUp, emailStep, router])

  const hasRendered = useHasRendered()
  const reason = searchParams.get('reason')

  return (
    <div className="relative mt-12 flex w-full flex-col items-center">
      <div data-enter className="w-full max-w-sm">
        <div className="flex flex-col items-center">
          {Boolean(reason) && <LoginReason reason={reason ?? ''} />}
          <ButtonLink
            unstyled
            focusStyled
            href="/"
            className="mb-14 shadow-modal rounded-[21px]"
          >
            <BlurredLogo />
          </ButtonLink>
          <div className="mb-[43px] text-center">
            <Heading as="h1" size="mini">
              {isSignUp ? 'Sign Up to BaseHub' : 'Log In to BaseHub'}
            </Heading>
            <p className="text-xs mt-2 text-dark-muted">
              {isSignUp ? (
                <>
                  Have an account?{' '}
                  <ButtonLink
                    unstyled
                    focusStyled
                    href={hasRendered ? getLoginHref(`/login`) : '/login'}
                    className="hover:text-dark-dim underline transition-colors"
                  >
                    Log In
                  </ButtonLink>
                  .
                </>
              ) : (
                <>
                  Don't have an account?{' '}
                  <ButtonLink
                    unstyled
                    focusStyled
                    href={hasRendered ? getLoginHref(`/signup`) : '/signup'}
                    className="hover:text-dark-dim underline transition-colors"
                  >
                    Sign Up
                  </ButtonLink>
                  .
                </>
              )}
            </p>
          </div>
          <div className="w-full">
            <SocialProviderLoginButton
              text="Continue with GitHub"
              loading={
                loadingProvider === 'github' ||
                loadingProviderFromQuery === 'github'
              }
              icon={<GitHubIcon className="absolute left-4 text-dark-muted" />}
              onClick={() => handleSignInWithProvider('github')}
            />
            <SocialProviderLoginButton
              loading={
                loadingProvider === 'google' ||
                loadingProviderFromQuery === 'google'
              }
              text="Continue with Google"
              icon={<GoogleIcon className="absolute left-4 text-dark-muted" />}
              onClick={() => handleSignInWithProvider('google')}
            />
          </div>
          <Separator className="my-8" />
          <EmailLoginProvider
            onNotAllowed={() => {
              setNotAllowed(true)
            }}
            emailStep={emailStep}
            setEmailStep={setEmailStep}
          />
          <div className={clsx(!isSignUp && 'invisible')}>
            <Separator className="my-8" />
            <p className="text-[12px] text-center text-dark-muted">
              By Signing Up, you agree to our{' '}
              <ButtonLink
                unstyled
                focusStyled
                href="/terms-and-conditions"
                className="border-b border-dark-faint hover:text-dark-dim hover:border-dark-muted transition-colors"
                target="_blank"
              >
                Terms of Service
              </ButtonLink>{' '}
              and{' '}
              <ButtonLink
                unstyled
                focusStyled
                href="/privacy"
                className="border-b border-dark-faint hover:text-dark-dim hover:border-dark-muted transition-colors"
                target="_blank"
              >
                Privacy Policy
              </ButtonLink>
              .
            </p>
          </div>
        </div>
      </div>

      {/* not allowed */}
      <LoginFormResponseContainer
        visible={notAllowed}
        back={{
          text: 'Back to login',
          handler: () => {
            setNotAllowed(false)
            router.replace(getLoginHref(loginRoute))
          },
        }}
      >
        <div className="flex flex-col items-center px-10 pt-10 pb-12 sm:pb-[85px]">
          <AlertIcon className="mb-6 text-white" />
          <p className="mb-4 mt-0 text-center text-lg font-medium">
            You don't have access to BaseHub
          </p>
          <p className="my-0 text-center text-base text-dark-muted">
            We&apos;re happy about your interest on trying BaseHub, but for the
            moment you don&apos;t have access to it.
          </p>
          <ButtonLink
            className="mt-10"
            size="medium"
            aria-label="join waitlist"
            href="/"
          >
            Join the waitlist
          </ButtonLink>
        </div>
      </LoginFormResponseContainer>
    </div>
  )
}

const LoginReason = ({ reason }: { reason: string }) => {
  const parts = reason.split(/<(.*?)>/)

  return (
    <div className="text-dark-muted text-sm border border-dark-border-solid py-2 px-3 bg-dark-base rounded-lg flex mb-12 text-pretty text-center">
      <p className="w-full">
        After logging in, you&apos;ll be able to{' '}
        {parts.map((part, index) => {
          if (index % 2 === 1) {
            return (
              <span
                key={index}
                className="text-dark-bright font-medium whitespace-nowrap"
              >
                {part}
              </span>
            )
          } else {
            return <span key={index}>{part}</span>
          }
        })}
        .
      </p>
    </div>
  )
}
